import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, UserCheck, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { listUsersSortBySchema, type TenantUser, type UserRole } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { useAuth } from '../../../auth/AuthContext';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../../components/ui/DataTable';
import { Dialog } from '../../../components/ui/Dialog';
import { InfoCell } from '../../../components/ui/InfoCell';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { StatusText } from '../../../components/ui/StatusText';
import { formatDay } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useListSearchParams } from '../../../lib/list-search-params';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { UserSheet } from './UserSheet';

/** Page, order and filters are the service's answer and live in the URL (plan §11 fix 12). */
export const UsersPage = () => {
  const { t } = useI18nTranslation();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const params = useListSearchParams();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<TenantUser | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const search = params.value('q');
  const roleFilter = params.value('role');
  const sortBy = params.sortBy(listUsersSortBySchema.options, 'name');
  const sortDirection = params.sortDirection('asc');

  const listQuery = useQuery(
    orpc.users.list.queryOptions({
      input: {
        page: params.page,
        pageSize: params.pageSize,
        sortBy,
        sortDirection,
        q: search.trim() === '' ? undefined : search.trim(),
        role: roleFilter === '' ? undefined : (roleFilter as UserRole),
      },
    }),
  );

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: orpc.users.list.key() });
  };

  const onMutationError = (error: unknown): void => setActionError(serverMessage(error) ?? t(submitErrorKey(error)));

  const updateRole = useMutation(orpc.users.updateRole.mutationOptions({ onSuccess: invalidate, onError: onMutationError }));

  const setStatus = useMutation(orpc.users.disable.mutationOptions({ onSuccess: invalidate, onError: onMutationError }));

  const resetPassword = useMutation(
    orpc.users.resetPassword.mutationOptions({
      onSuccess: async (result) => {
        setTemporaryPassword(result.temporaryPassword);
        await invalidate();
      },
      onError: onMutationError,
    }),
  );

  const columns = useMemo<DataTableColumns<TenantUser>>(
    () => [
      {
        // The id doubles as the service's `sortBy` value, which names this column by what it sorts on.
        id: 'name',
        header: t('users.columnUser'),
        accessorFn: (row) => row.name,
        cell: ({ row }) => (
          <InfoCell
            title={row.original.name}
            subline={row.original.email}
          />
        ),
      },
      {
        id: 'role',
        header: t('users.role'),
        accessorFn: (row) => row.role,
        cell: ({ row }) => (
          <Select
            aria-label={t('users.role')}
            className="h-8 w-36 text-xs"
            value={row.original.role}
            disabled={row.original.id === currentUser?.id || updateRole.isPending}
            onChange={(event) => {
              setActionError(null);
              updateRole.mutate({ userId: row.original.id, role: event.target.value as UserRole });
            }}
            options={[
              { value: 'SALES', label: t('roles.SALES') },
              { value: 'ADMIN', label: t('roles.ADMIN') },
            ]}
          />
        ),
      },
      {
        id: 'status',
        header: t('users.status'),
        // The service's `sortBy` enum has no status order.
        enableSorting: false,
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <StatusText
              label={t(`users.statuses.${row.original.status}`)}
              tone={row.original.status === 'ACTIVE' ? 'muted' : 'danger'}
            />
            {row.original.mustChangePassword ? <Badge tone="warning">{t('users.mustChangePassword')}</Badge> : null}
          </div>
        ),
      },
      {
        id: 'createdAt',
        header: t('users.joined'),
        accessorFn: (row) => row.createdAt,
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{formatDay(row.original.createdAt)}</span>,
      },
      {
        id: 'actions',
        header: t('users.actions'),
        enableSorting: false,
        cell: ({ row }) => {
          const isSelf = row.original.id === currentUser?.id;
          const isDisabled = row.original.status === 'DISABLED';

          return (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={isSelf || resetPassword.isPending}
                onClick={() => {
                  setActionError(null);
                  setTemporaryPassword(null);
                  setResetTarget(row.original);
                }}
              >
                <KeyRound className="size-3.5" />
                {t('users.resetPassword')}
              </Button>
              <Button
                variant={isDisabled ? 'outline' : 'ghost'}
                size="sm"
                disabled={isSelf || setStatus.isPending}
                onClick={() => {
                  setActionError(null);
                  setStatus.mutate({ userId: row.original.id, status: isDisabled ? 'ACTIVE' : 'DISABLED' });
                }}
              >
                {isDisabled ? <UserCheck className="size-3.5" /> : <UserX className="size-3.5" />}
                {isDisabled ? t('users.enable') : t('users.disable')}
              </Button>
            </div>
          );
        },
      },
    ],
    [currentUser?.id, resetPassword.isPending, setStatus, t, updateRole],
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('users.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('users.subtitle')}</p>
        </div>
        <Button onClick={() => setIsSheetOpen(true)}>
          <Plus className="size-4" />
          {t('users.create')}
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <label
            htmlFor="users-search"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('users.search')}
          </label>
          <Input
            id="users-search"
            value={search}
            placeholder={t('users.searchPlaceholder')}
            onChange={(event) => params.update({ q: event.target.value })}
          />
        </div>
        <div className="w-44">
          <label
            htmlFor="users-role-filter"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('users.role')}
          </label>
          <Select
            id="users-role-filter"
            value={roleFilter}
            placeholder={t('common.all')}
            onChange={(event) => params.update({ role: event.target.value })}
            options={[
              { value: 'ADMIN', label: t('roles.ADMIN') },
              { value: 'SALES', label: t('roles.SALES') },
            ]}
          />
        </div>
      </div>

      {actionError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </p>
      ) : null}

      {listQuery.isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>
      ) : null}

      <DataTable
        columns={columns}
        data={listQuery.data?.items ?? []}
        isLoading={listQuery.isLoading}
        emptyMessage={t('users.empty')}
        pagination={{ page: params.page, pageSize: params.pageSize, total: listQuery.data?.meta.total ?? 0, onPageChange: params.setPage }}
        sorting={{ sortBy, sortDirection, onSortChange: params.setSort }}
      />

      <UserSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
      />

      <Dialog
        isOpen={resetTarget !== null}
        onClose={() => {
          setResetTarget(null);
          setTemporaryPassword(null);
        }}
        title={t('users.resetTitle')}
        description={temporaryPassword ? undefined : t('users.resetBody', { name: resetTarget?.name ?? '' })}
        footer={
          temporaryPassword ? (
            <Button
              onClick={() => {
                setResetTarget(null);
                setTemporaryPassword(null);
              }}
            >
              {t('actions.close')}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setResetTarget(null)}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                isLoading={resetPassword.isPending}
                onClick={() => {
                  if (resetTarget) {
                    resetPassword.mutate({ userId: resetTarget.id });
                  }
                }}
              >
                {t('users.resetConfirm')}
              </Button>
            </>
          )
        }
      >
        {temporaryPassword ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('users.resetDone', { name: resetTarget?.name ?? '' })}</p>
            <p
              dir="ltr"
              className="rounded-md border border-border bg-muted px-3 py-2 text-center font-mono text-base tracking-wider text-foreground"
            >
              {temporaryPassword}
            </p>
            <p className="text-xs text-muted-foreground">{t('users.resetOnce')}</p>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};
